const STORAGE_KEY = 'pendingSubmitDownloads';

let requestedInThisTab = [];

/**
 * Reads the pending list, tolerating storage being unavailable or holding rubbish.
 *
 * @returns {string[]} the request ids still waiting; [] when storage is unavailable or does not hold a list.
 */
function read() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string' && id.length > 0) : [];
  } catch (error) {
    return [];
  }
}

/**
 * Writes the pending list.
 *
 * @param {string[]} ids - the request ids still waiting.
 * @returns {boolean} false when the browser refuses storage (private window, blocked site data, full quota).
 */
function write(ids) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Remembers a download that has been accepted but not yet delivered.
 *
 * The socket event is how a finished download normally arrives, but a tab that was backgrounded,
 * reloaded or briefly offline never hears it. Keeping the id lets the page ask afterwards instead
 * of leaving the user waiting on a file that is already sitting in S3.
 *
 * Session storage rather than local: a pending download belongs to the tab that asked for it and
 * should not outlive the session.
 *
 * @param {string} requestId - the accepted request.
 * @returns {boolean} true once the id is stored; false for an empty id or when the browser refuses storage,
 * in which case the download still arrives over the socket but cannot be recovered after a reload.
 */
export function rememberPendingDownload(requestId) {
  if (!requestId) {
    return false;
  }
  if (!requestedInThisTab.includes(requestId)) {
    requestedInThisTab = [...requestedInThisTab, requestId];
  }
  const ids = read();
  if (ids.includes(requestId)) {
    return true;
  }
  return write([...ids, requestId]);
}

/**
 * Forgets a download that has been delivered or has failed.
 *
 * @param {string} requestId - the request to drop.
 * @returns {void}
 */
export function forgetPendingDownload(requestId) {
  requestedInThisTab = requestedInThisTab.filter((id) => id !== requestId);
  write(read().filter((id) => id !== requestId));
}

/**
 * Lists the downloads still waiting to be delivered.
 *
 * @returns {string[]} the request ids.
 */
export function listPendingDownloads() {
  return read();
}

/**
 * Drops every pending download.
 *
 * @returns {void}
 */
export function clearPendingDownloads() {
  requestedInThisTab = [];
  write([]);
}

/**
 * Tells whether this tab asked for a download, so a finished-download event is acted on only by
 * the tab that requested it rather than by every tab the same user has open.
 *
 * @param {string} requestId - the request the event is about.
 * @returns {boolean} true when this tab requested it, either in this page's lifetime or before a reload.
 */
export function isPendingInThisTab(requestId) {
  if (!requestId) {
    return false;
  }
  return requestedInThisTab.includes(requestId) || read().includes(requestId);
}
